import { Link } from "react-router-dom";
import { Spin, Empty } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sinceWhen } from "services/general";
import {
  TONE_STYLES,
  deriveNotification,
} from "modules/topbar/utils/notifications-display";

// A modified click opens a new tab or window, so the popover stays open for the next one.
const isModifiedClick = (event) =>
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey ||
  event.button !== 0;

/**
 * NotificationsPanel — the notifications header/list/footer shared by
 * NotificationsPopupMenu (tablet/desktop) and NotificationsMobileDrawer
 * (phone), so the two breakpoint-specific shells never drift apart.
 */
const NotificationsPanel = ({
  t,
  count,
  loading,
  markAllRead,
  notifications,
  fetchLoading,
  handleOpenNotification,
  handleViewAll,
  showTitle = true,
  listClassName = "max-h-[380px] overflow-y-auto",
}) => (
  <>
    {showTitle && (
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light shrink-0">
        <span className="text-sm font-semibold text-heading">
          {t("settings.notifications")}
        </span>
        {count > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            disabled={loading}
            className="text-xs text-primary font-medium hover:underline disabled:opacity-50"
          >
            {t("amerSupport.mark_all_read")}
          </button>
        )}
      </div>
    )}
    {!showTitle && count > 0 && (
      <div className="flex items-center justify-end px-4 py-3 border-b border-border-light shrink-0">
        <button
          type="button"
          onClick={markAllRead}
          disabled={loading}
          className="text-xs text-primary font-medium hover:underline disabled:opacity-50"
        >
          {t("amerSupport.mark_all_read")}
        </button>
      </div>
    )}

    <div className={listClassName}>
      {fetchLoading ? (
        <div className="flex justify-center py-8">
          <Spin size="small" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="py-8">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("amerSupport.no_notifications")}
          />
        </div>
      ) : (
        notifications.map((n) => {
          const {
            title,
            message,
            typeLabel,
            statusText,
            tone,
            actionUrl,
            icon,
          } = deriveNotification(n);
          const clickable = Boolean(actionUrl);
          const toneStyle = TONE_STYLES[tone];
          const unread = !n.read_at;
          const Wrapper = clickable ? Link : "div";
          const handleLinkClick = (event) => {
            if (!isModifiedClick(event)) handleOpenNotification();
          };
          return (
            <Wrapper
              key={n.id}
              {...(clickable
                ? { to: actionUrl, onClick: handleLinkClick }
                : {})}
              className={`group relative w-full px-4 py-3 border-b border-border-light flex items-start gap-3 ${
                unread ? "bg-primary-bg-soft/40" : ""
              } ${clickable ? "cursor-pointer hover:bg-primary-bg-soft/60 transition-colors" : ""}`}
            >
              {unread && (
                <span
                  className={`absolute top-0 bottom-0 start-0 w-1 ${toneStyle.dot}`}
                  aria-hidden="true"
                />
              )}
              <span
                className={`mt-0.5 shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${toneStyle.well}`}
              >
                <FontAwesomeIcon icon={icon} className="text-[14px]" />
              </span>
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-heading truncate flex-1">
                    {title || t("settings.notifications")}
                  </span>
                  {unread && (
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${toneStyle.dot}`}
                      aria-hidden="true"
                    />
                  )}
                </div>
                {message ? (
                  <span className="text-xs text-muted line-clamp-2 leading-relaxed">
                    {message}
                  </span>
                ) : null}
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted">
                  {statusText ? (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${toneStyle.well}`}
                    >
                      {statusText}
                    </span>
                  ) : null}
                  {typeLabel ? (
                    <span className="truncate">{typeLabel}</span>
                  ) : null}
                  {typeLabel && n.created_at ? <span>·</span> : null}
                  {n.created_at ? <span>{sinceWhen(n.created_at)}</span> : null}
                  {clickable ? (
                    <span className="ms-auto text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      {t("view")}
                    </span>
                  ) : null}
                </div>
              </div>
            </Wrapper>
          );
        })
      )}
    </div>

    <div className="border-t border-border-light shrink-0">
      <button
        type="button"
        onClick={handleViewAll}
        className="w-full px-4 py-3 text-xs font-semibold text-primary hover:bg-primary-bg-soft/60 transition-colors text-center"
      >
        {t("notificationsCenter.view_all")}
      </button>
    </div>
  </>
);

export default NotificationsPanel;
